import { useState } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const testimonials = [
  {
    quote: "Working with Talo Architect was a transformative experience. They listened to everything we envisioned and translated it into a home that exceeds every expectation. The attention to detail and quality of design is extraordinary.",
    name: 'Sarah & Michael T.',
    location: 'Custom Home · Scarsdale, NY',
    initial: 'S',
  },
  {
    quote: "Talo Architect transformed our outdated colonial into a modern, light-filled home that fits perfectly in the neighborhood. The design process was collaborative, transparent, and delivered exactly on time.",
    name: 'David & Jennifer K.',
    location: 'Renovation & Addition · White Plains, NY',
    initial: 'D',
  },
  {
    quote: "From first consultation to final walkthrough, every decision was purposeful and precise. Their design instincts are impeccable — they saw possibilities in our property that we never imagined ourselves.",
    name: 'Robert L.',
    location: 'New Home Design · Westchester, NY',
    initial: 'R',
  },
];

export default function Testimonials() {
  const [active, setActive] = useState(0);
  const headerRef = useScrollReveal();

  const prev = () => setActive((a) => (a - 1 + testimonials.length) % testimonials.length);
  const next = () => setActive((a) => (a + 1) % testimonials.length);

  return (
    <section className="py-36 bg-dark-ink relative overflow-hidden">
      {/* Decorative bg image */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.07]"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/eaea25c27ecb26b4c9b5bd933b44b439.jpg)',
        }}
      />

      {/* Large decorative quote mark */}
      <div className="absolute top-16 left-1/2 -translate-x-1/2 font-serif text-[280px] leading-none text-white/[0.03] select-none pointer-events-none">
        &ldquo;
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-8 lg:px-16">
        {/* Header */}
        <div ref={headerRef} className="reveal-on-scroll text-center mb-20">
          <p className="font-sans text-[10px] tracking-[0.22em] uppercase text-bronze mb-4">Client Voices</p>
          <h2 className="font-serif text-5xl lg:text-6xl font-light text-white">What Clients Say</h2>
        </div>

        {/* Desktop: 3 cards */}
        <div className="hidden lg:grid grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <TestimonialCard key={i} testimonial={t} active={i === active} onClick={() => setActive(i)} delay={i + 1} />
          ))}
        </div>

        {/* Mobile: single carousel */}
        <div className="lg:hidden">
          <div className="border border-white/10 p-8 bg-white/[0.04]">
            {/* Stars */}
            <div className="flex items-center gap-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-bronze text-sm" />
                </div>
              ))}
            </div>
            <p className="font-serif text-lg font-light text-white/85 leading-relaxed italic mb-8">
              &ldquo;{testimonials[active].quote}&rdquo;
            </p>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-bronze/30 flex items-center justify-center flex-shrink-0">
                <span className="font-serif text-base text-white/80">{testimonials[active].initial}</span>
              </div>
              <div>
                <p className="font-sans text-sm font-medium text-white/80">{testimonials[active].name}</p>
                <p className="font-sans text-[11px] text-white/40">{testimonials[active].location}</p>
              </div>
            </div>
          </div>

          {/* Mobile navigation */}
          <div className="flex items-center justify-center gap-6 mt-8">
            <button onClick={prev} className="w-10 h-10 flex items-center justify-center border border-white/20 text-white/50 hover:text-white hover:border-white/50 transition-colors cursor-pointer">
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${i === active ? 'w-6 h-2 bg-bronze' : 'w-2 h-2 bg-white/20 hover:bg-white/40'}`}
                />
              ))}
            </div>
            <button onClick={next} className="w-10 h-10 flex items-center justify-center border border-white/20 text-white/50 hover:text-white hover:border-white/50 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({
  testimonial,
  active,
  onClick,
  delay,
}: {
  testimonial: (typeof testimonials)[0];
  active: boolean;
  onClick: () => void;
  delay: number;
}) {
  const ref = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`reveal-on-scroll reveal-delay-${delay} p-8 border cursor-pointer transition-all duration-500 ${
        active
          ? 'border-bronze/50 bg-white/[0.07]'
          : 'border-white/8 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.05]'
      }`}
      onClick={onClick}
    >
      {/* Stars */}
      <div className="flex items-center gap-1 mb-6">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="w-4 h-4 flex items-center justify-center">
            <i className="ri-star-fill text-bronze text-xs" />
          </div>
        ))}
      </div>

      {/* Quote */}
      <p className="font-serif text-base font-light text-white/75 leading-relaxed italic mb-8 line-clamp-3">
        &ldquo;{testimonial.quote}&rdquo;
      </p>

      {/* Divider */}
      <div className="w-8 h-px bg-bronze/40 mb-6" />

      {/* Author */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-bronze/25 flex items-center justify-center flex-shrink-0">
          <span className="font-serif text-sm text-white/70">{testimonial.initial}</span>
        </div>
        <div>
          <p className="font-sans text-xs font-medium text-white/75 tracking-wide">{testimonial.name}</p>
          <p className="font-sans text-[11px] text-white/35 mt-0.5">{testimonial.location}</p>
        </div>
      </div>
    </div>
  );
}
